import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { translate } from '@utils/i18n';
import { Button } from '@ui/atoms/Button';
import { ConfirmDialog } from '@ui/molecules/ConfirmDialog';
import { useInspectionAttachments } from './hooks/useInspectionAttachments';
import { useCreateAttachment, useUpdateAttachment, useDeleteAttachment } from './hooks/useAttachmentMutations';
import { AttachmentsTable } from './AttachmentsTable';
import { AttachmentBrushaway } from './AttachmentBrushaway';
import { SubcontractorDocumentsSection } from './SubcontractorDocumentsSection';
const namespace = 'features.inspections_attachments';
export function InspectionAttachments({ inspectionId, hasCoverLetter }) {
    var _a;
    const { data: attachments, isLoading } = useInspectionAttachments(inspectionId);
    const createMutation = useCreateAttachment(inspectionId);
    const updateMutation = useUpdateAttachment(inspectionId);
    const deleteMutation = useDeleteAttachment(inspectionId);
    const [brushawayOpen, setBrushawayOpen] = useState(false);
    const [editingAttachment, setEditingAttachment] = useState(null);
    const [brushawayKey, setBrushawayKey] = useState(0);
    const [deletingAttachment, setDeletingAttachment] = useState(null);
    const handleAddClick = () => {
        setEditingAttachment(null);
        setBrushawayKey((k) => k + 1);
        setBrushawayOpen(true);
    };
    const handleEditClick = (attachment) => {
        setEditingAttachment(attachment);
        setBrushawayKey((k) => k + 1);
        setBrushawayOpen(true);
    };
    const handleDeleteClick = (attachment) => {
        setDeletingAttachment(attachment);
    };
    const handleConfirmDelete = () => {
        if (!deletingAttachment)
            return;
        deleteMutation.mutate(deletingAttachment.id, {
            onSuccess: () => setDeletingAttachment(null)
        });
    };
    const handleClose = () => {
        setBrushawayOpen(false);
        setEditingAttachment(null);
    };
    const handleSave = (form) => {
        if (editingAttachment) {
            updateMutation.mutate({ id: editingAttachment.id, form }, { onSuccess: handleClose });
        }
        else {
            createMutation.mutate(form, { onSuccess: handleClose });
        }
    };
    const isSaving = createMutation.isPending || updateMutation.isPending;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "ia-scope attachment-section" },
            React.createElement("div", { className: "attachment-section__header" },
                React.createElement("h4", { className: "header blue bolder smaller", style: { paddingBottom: '10px' } },
                    translate('section_title', { namespace }),
                    React.createElement(Button, { variant: "primary", onClick: handleAddClick },
                        React.createElement("i", { className: "fa-solid fa-plus", "aria-hidden": "true" }),
                        translate('add_button', { namespace })))),
            isLoading ? (React.createElement("div", { className: "workspace__section" },
                React.createElement("div", { className: "qmb-loading--96", "data-title": translate('loading', { namespace: 'generic' }) },
                    React.createElement("svg", { role: "img" },
                        React.createElement("use", { href: "/map.svg#load-spinner" }))))) : (React.createElement(AttachmentsTable, { attachments: attachments !== null && attachments !== void 0 ? attachments : [], onEdit: handleEditClick, onDelete: handleDeleteClick })),
            React.createElement(SubcontractorDocumentsSection, { inspectionId: inspectionId })),
        brushawayOpen &&
            createPortal(React.createElement("div", { className: "attachment-brushaway-backdrop", "aria-hidden": "true" }), document.body),
        React.createElement(ConfirmDialog, { isOpen: !!deletingAttachment, title: translate('delete_title', { namespace }), description: translate('delete_description', { namespace, name: (_a = deletingAttachment === null || deletingAttachment === void 0 ? void 0 : deletingAttachment.name) !== null && _a !== void 0 ? _a : '' }), confirmLabel: translate('delete_confirm', { namespace }), cancelLabel: translate('delete_cancel', { namespace }), confirmVariant: "danger", loading: deleteMutation.isPending, onConfirm: handleConfirmDelete, onCancel: () => setDeletingAttachment(null) }),
        React.createElement(AttachmentBrushaway, { key: brushawayKey, isOpen: brushawayOpen, attachment: editingAttachment, hasCoverLetter: hasCoverLetter, isSaving: isSaving, onClose: handleClose, onSave: handleSave })));
}
